import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/button.tsx';
import { Card, CardContent, CardHeader } from '../../components/ui/card.tsx';
import type { ApiError } from '../../lib/api.ts';
import { useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useMe, useTwoFactorEnable, useTwoFactorSetup } from '../../lib/queries.ts';

const inputClass =
  'h-12 w-44 rounded-md border border-input bg-card px-3 font-mono tracking-widest lg:h-10 lg:w-40';

/**
 * The signed-in account's security: turning two-step verification on (it is turned off by an
 * administrator).
 */
export function SecurityPage() {
  const t = useT();
  usePageTitle(t.security.title);
  const me = useMe();
  const setup = useTwoFactorSetup();
  const enable = useTwoFactorEnable();
  const [code, setCode] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    enable.mutate(code);
  };
  const problem = (error: ApiError): string =>
    error.status === 503
      ? t.security.unavailable
      : error.status === 422
        ? t.security.wrongCode
        : t.security.failed;

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.security.title}</h1>
      <p className="text-sm text-muted-foreground">{t.security.intro}</p>
      <Card className="max-w-xl">
        <CardHeader>
          <h2 className="text-lg font-semibold">{t.security.twoFactorTitle}</h2>
          <p className="text-sm text-muted-foreground">{t.security.twoFactorIntro}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {me.data?.twoFactorEnabled ? (
            <div role="status" className="space-y-1">
              <p className="font-medium">{t.security.enabled}</p>
              <p className="text-sm text-muted-foreground">{t.security.enabledHint}</p>
            </div>
          ) : !setup.data ? (
            <>
              <p>{t.security.disabled}</p>
              {setup.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {problem(setup.error)}
                </p>
              ) : null}
              <Button onClick={() => setup.mutate()} disabled={setup.isPending}>
                {setup.isPending ? t.security.starting : t.security.start}
              </Button>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm">{t.security.step1}</p>
              <p className="text-sm">
                <span className="text-muted-foreground">{t.security.secretLabel}: </span>
                <code className="select-all break-all font-mono">{setup.data.secret}</code>
              </p>
              <p className="text-sm">{t.security.step2}</p>
              <div className="space-y-1">
                <label htmlFor="code" className="text-sm font-medium">
                  {t.security.codeLabel}
                </label>
                <div>
                  <input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className={inputClass}
                  />
                </div>
              </div>
              {enable.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {problem(enable.error)}
                </p>
              ) : null}
              <Button type="submit" disabled={enable.isPending || code.length !== 6}>
                {enable.isPending ? t.security.confirming : t.security.confirm}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </>
  );
}
